import { inject, Injectable } from '@angular/core';

import { environment } from 'src/environments/environment';
import { AttributeDataService } from './attribute-data.service';

// Visible area of an image, as fractions (0 to 1) of the full image
export interface ImageCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Rules for cropping a user-added image by dragging its sides in the preview.
// Hidden behind the RESIZE_BEHAVIOR_ENABLED release flag until release.
@Injectable({
  providedIn: 'root',
})
export class ImageCropService {
  // Name of the preview iframe URL parameter that turns the new resize behaviour
  // on in common-template. Present only when the flag is on.
  static readonly PREVIEW_URL_PARAM = 'resizeBehavior';

  // Same limits as rise-folder's normalizeCrop (rise-lit-components,
  // src/common/image-crop.js), which ignores any crop that breaks them
  private static readonly MIN_CROP_SIZE = 0.01;
  private static readonly EDGE_TOLERANCE = 1e-6;

  private attributeDataService = inject(AttributeDataService);

  isEnabled(): boolean {
    return environment.RESIZE_BEHAVIOR_ENABLED === true;
  }

  // Only a user-added image component can be cropped. The preview also checks
  // that it holds a single image before sending a crop.
  isCroppableComponent(componentId: string): boolean {
    return (
      this.attributeDataService.getAttributeData(
        componentId,
        'userComponent',
      ) === true &&
      this.attributeDataService.getAttributeData(componentId, 'tagName') ===
        'rise-folder'
    );
  }

  // Returns the crop as { x, y, width, height } (any other keys dropped), or
  // null when it is missing, partial or out of range
  getValidCrop(value: unknown): ImageCrop | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return null;
    }

    const { x, y, width, height } = value as Record<string, unknown>;

    if (
      ![x, y, width, height].every(
        (item) => typeof item === 'number' && Number.isFinite(item),
      )
    ) {
      return null;
    }

    const crop = { x, y, width, height } as ImageCrop;

    if (
      crop.width < ImageCropService.MIN_CROP_SIZE ||
      crop.height < ImageCropService.MIN_CROP_SIZE ||
      crop.x < 0 ||
      crop.y < 0 ||
      crop.x + crop.width > 1 + ImageCropService.EDGE_TOLERANCE ||
      crop.y + crop.height > 1 + ImageCropService.EDGE_TOLERANCE
    ) {
      return null;
    }

    return {
      x: crop.x,
      y: crop.y,
      width: Math.min(crop.width, 1 - crop.x),
      height: Math.min(crop.height, 1 - crop.y),
    };
  }
}
